import { useEffect } from "react";

/**
 * Message type the embedding page listens for. The AMP "survey" component's
 * resize listener (renderSurveyScript) matches on this exact string.
 */
const EMBED_HEIGHT_MESSAGE_TYPE = "cpp-embed-height";

/**
 * When this page runs inside an iframe (i.e. embedded on another Ring page),
 * measure the document's content height and post it to the parent whenever it
 * changes — on mount, on load, and on any reflow (responsive text wrapping,
 * async content, fonts settling, validation errors expanding the form). The
 * embedding page resizes the iframe to match, so there is no fixed-height gap
 * above/below the content.
 *
 * No-op when not embedded (parent === self). Height is sent with targetOrigin
 * "*" because it is non-sensitive; the embedding page validates the sender
 * origin before trusting it.
 */
export const useEmbedAutoResize = (): void => {
  useEffect(() => {
    // Only emit when embedded in an iframe (the parent is a different window).
    if (window.parent === window) {
      return;
    }

    const postHeight = (): void => {
      // Measure the BODY's content height, not documentElement.scrollHeight.
      // The root <html> element's scrollHeight is clamped to at least the
      // viewport (iframe) height, so for content shorter than the frame it
      // reports the frame height instead of the content — which would keep the
      // iframe stuck at its fallback size. document.body.scrollHeight is the
      // true content height and still includes overflow when content is taller.
      const height = Math.ceil(document.body.scrollHeight);
      window.parent.postMessage(
        { type: EMBED_HEIGHT_MESSAGE_TYPE, height },
        "*"
      );
    };

    // Re-measure whenever the body's size changes (covers reflow, async content,
    // and form expansion). ResizeObserver fires an initial callback on observe.
    const observer = new ResizeObserver(() => postHeight());
    observer.observe(document.body);

    // Also post once after full load, for late-arriving assets/fonts.
    window.addEventListener("load", postHeight);
    postHeight();

    return () => {
      observer.disconnect();
      window.removeEventListener("load", postHeight);
    };
  }, []);
};
